import React, { useEffect, useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, ChevronRight, Search, Truck, Star } from 'lucide-react';
import { SERVICE_CITIES, SERVICE_PRODUCT_SLUGS, getServiceProduct, type ServiceCity } from '../data/cityMaterialContent';
import { getRegionMeta } from '../utils/region';
// Sitemap-style index page that links to every generated City × Material
// landing page. The layout splits into a FEATURED row of 3 flagship cities
// (one per region — Pierce home, Seattle flagship, Eastside flagship)
// followed by a compact two-column county-grouped list of the rest. The
// rhythm change keeps the page from feeling like a wall of identical cards.
// Display order of the featured cities at the top of /areas. The filter
// below preserves THIS order (not the order cities appear in
// SERVICE_CITIES) so we can lead with the biggest-market cities.
const FEATURED_SLUGS = ['seattle', 'bellevue', 'tacoma'];
// Subtle zone-driven accent so customers can scan delivery zones across
// the page at a glance. Stays muted — never competes with brand-orange.
// Three delivery tiers: Zone 1 inner (+$80), Zone 2 middle (+$120),
// Zone 3 outer (+$175). Curated city.zone values (legacy 1–5) still map
// onto the correct tier's accent + fee: old 1/2 → inner, old 3 → middle,
// old 4/5 → outer.
const ZONE_ACCENT: Record<number, string> = {
  1: 'border-l-[#04A05C]',
  2: 'border-l-[#04A05C]',
  3: 'border-l-amber-400',
  4: 'border-l-rose-400',
  5: 'border-l-rose-400'
};
const ZONE_FEE: Record<number, string> = {
  1: '+$80',
  2: '+$80',
  3: '+$120',
  4: '+$175',
  5: '+$175'
};
export function ServiceAreas() {
  const [query, setQuery] = useState('');
  const regionMeta = getRegionMeta();
  const regionSlugs = regionMeta.cities.map(c => c.toLowerCase().replace(/\s+/g, '-'));
  const FEATURED_SLUGS = Array.from(new Set([...regionSlugs, 'seattle', 'bellevue', 'tacoma'])).slice(0, 3);
  useEffect(() => {
    const orig = document.title;
    document.title = 'Service Areas — 206Gravel Delivery to Tacoma, Seattle, Bellevue, Puyallup & more';
    return () => {
      document.title = orig;
    };
  }, []);
  const products = useMemo(() => SERVICE_PRODUCT_SLUGS.map(s => getServiceProduct(s)).filter(Boolean) as {
    product: any;
    category: any;
  }[], []);
  const matches = (city: ServiceCity) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return city.name.toLowerCase().includes(q) || city.county.toLowerCase().includes(q) || city.neighborhoods.some(n => n.toLowerCase().includes(q));
  };
  // Map over FEATURED_SLUGS (not filter on SERVICE_CITIES) so the
  // display order respects the curated lead-with list above.
  const featured = FEATURED_SLUGS.map(slug => SERVICE_CITIES.find(c => c.slug === slug)).filter(Boolean) as typeof SERVICE_CITIES;
  const featuredVisible = featured.filter(matches);
  // Non-featured grouped by county for the secondary section.
  const remainingByCounty = useMemo(() => {
    const remaining = SERVICE_CITIES.filter(c => !FEATURED_SLUGS.includes(c.slug) && matches(c));
    return {
      King: remaining.filter(c => c.county === 'King'),
      Pierce: remaining.filter(c => c.county === 'Pierce')
    };
  }, [query]);
  const totalMatches = featuredVisible.length + remainingByCounty.King.length + remainingByCounty.Pierce.length;
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-3166" data-mp="6teb4jk5v8k">
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-3167" data-mp="3rdnobrw8qa1s">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-3168" data-mp="a50sjfmgpz6v">
          <li data-id="element-3169" data-mp="554lf7igxrn3">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3170" data-mp="54mglmhqq53k">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-3171" data-mp="1li30jmahxgff" />
          <li className="text-stone-900 dark:text-white font-semibold" aria-current="page" data-id="element-3172" data-mp="1glciwuj1mcrw">
            Service Areas
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8" data-id="element-3173" data-mp="3r86m43f3kuy6">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-3174" data-mp="3raao2ftfrjvt">
          <div className="inline-flex items-center gap-2 mb-3" data-id="element-3175" data-mp="449xa8j93vp5">
            <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-3176" data-mp="933cc5e6rurf" />
            <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-3177" data-mp="90h2u07qj56k">
              Puget Sound · King &amp; Pierce County
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-3178" data-mp="it6disvgof0u">
            Where we deliver
          </h1>
          <p className="mt-4 text-stone-600 dark:text-stone-300 max-w-3xl" data-id="element-3179" data-mp="rumbu2904qop">
            We deliver gravel, topsoil, bark, and drain rock across South King
            and Pierce County. Tap a city to see pricing, materials, and
            delivery details specific to your neighborhood.
          </p>
        </motion.div>

        {/* Search + zone legend */}
        <div className="mt-6 grid md:grid-cols-[1fr,auto] gap-4 items-center" data-id="element-3180" data-mp="wx527jnhmgr4">
          <div className="relative" data-id="element-3181" data-mp="swl5gh6ttf5i">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-3182" data-mp="sxmu5ogrm05p" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search a city or neighborhood…" className="w-full pl-10 pr-4 py-3 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-orange" data-id="element-3183" data-mp="1pl9a8btj75wz" />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] font-bold text-stone-600 dark:text-stone-300" data-id="element-3184" data-mp="28dbxw74lbmgq">
            <span className="uppercase tracking-wider text-stone-400 dark:text-stone-500" data-id="element-3185" data-mp="23lk4lou1v2mw">
              Zone fees
            </span>
            {([1, 2, 3] as const).map(z => <span key={z} className="inline-flex items-center gap-1.5" data-id="element-3186" data-mp="6ofost4387gw">
                <span className={`block w-2 h-2 rounded-full ${ZONE_ACCENT[z].replace('border-l-', 'bg-')}`} data-id="element-3187" data-mp="3o4e3qslmx6zz" />
                Z{z} {ZONE_FEE[z]}
              </span>)}
          </div>
        </div>
      </section>

      {/* Featured cities */}
      {featuredVisible.length > 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10" data-id="element-3188" data-mp="3f2ejzsj8jotc">
          <div className="flex items-center gap-2 mb-4" data-id="element-3189" data-mp="3k3kffy4a5q6k">
            <Star className="w-4 h-4 text-brand-orange" data-id="element-3190" data-mp="355jrbq9n8imz" />
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-orange" data-id="element-3191" data-mp="35737zhz7ovpa">
              Featured cities
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4" data-id="element-3192" data-mp="13dnfefjkd6ro">
            {featuredVisible.map((city, idx) => <motion.div key={city.slug} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.05 * idx
        }} id={city.slug} className={`scroll-mt-24 p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 border-l-4 ${ZONE_ACCENT[city.zone]}`} data-id="element-3193" data-mp="18czyqqb87vpi" data-mpi={idx}>
                <div className="flex items-start justify-between gap-3 mb-4" data-id="element-3194" data-mp="2xtvmeuj0arlg">
                  <div data-id="element-3195" data-mp="3blsb0olgujd3">
                    <h3 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-3196" data-mp="3bl0m1wmdjjvs">
                      {city.name}
                    </h3>
                    <p className="mt-1 text-[11px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider" data-id="element-3197" data-mp="3ljptevgn9lxs">
                      {city.county} County · Zone {city.zone}
                    </p>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-stone-100 dark:bg-white/[0.06] text-[11px] font-bold text-stone-700 dark:text-stone-200 shrink-0" data-id="element-3198" data-mp="1r6zzidqa0cqe">
                    {ZONE_FEE[city.zone]}
                  </span>
                </div>

                <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed mb-4" data-id="element-3199" data-mp="10bu3bay0fa5u">
                  {city.blurb}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-5" data-id="element-3200" data-mp="1a4j6zjnbjjqm">
                  {city.neighborhoods.slice(0, 4).map(n => <span key={n} className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-xs font-medium text-stone-600 dark:text-stone-400" data-id="element-3201" data-mp="1f19jxy3ttz7t">
                      {n}
                    </span>)}
                  {city.neighborhoods.length > 4 && <span className="px-2 py-0.5 text-[10px] font-medium text-stone-400" data-id="element-3202" data-mp="rd01951wtrkw">
                      +{city.neighborhoods.length - 4} more
                    </span>}
                </div>

                <div className="grid grid-cols-2 gap-1.5 mb-4" data-id="element-3203" data-mp="22s5j5gam3wp3">
                  {products.map(({
              product
            }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="group px-3 py-2 rounded-lg bg-stone-50 dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-3204" data-mp="27qqildi1icch">
                      <div className="text-xs font-bold text-stone-900 dark:text-white truncate group-hover:text-brand-orange transition-colors" data-id="element-3205" data-mp="3ncalahntj4s8">
                        {product.name}
                      </div>
                      <div className="text-[10px] text-stone-500 dark:text-stone-400" data-id="element-3206" data-mp="38lbi5m5r58g1">
                        ${product.pricePerYard}/yd
                      </div>
                    </Link>)}
                </div>

                <Link to={`/delivery-zones?address=${encodeURIComponent(city.geocodeHint)}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange hover:underline" data-id="element-3207" data-mp="2zkdamtw3uisp">
                  See {city.name} on the zone map
                  <ArrowRight className="w-3 h-3" data-id="element-3208" data-mp="xrxflz4n2chs" />
                </Link>
              </motion.div>)}
          </div>
        </section>}

      {/* More service areas — county-grouped */}
      {(remainingByCounty.Pierce.length > 0 || remainingByCounty.King.length > 0) && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-3209" data-mp="3fh1tppwtaoye">
          <div className="flex items-center gap-2 mb-4" data-id="element-3210" data-mp="3fiwdh0wckb0w">
            <Truck className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-3211" data-mp="3tf08uxiyipey" />
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400" data-id="element-3212" data-mp="3peg8zm7017p4">
              More service areas
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-3213" data-mp="2wq0ud0rs4reb">
            {(['King', 'Pierce'] as const).map(county => {
          const list = remainingByCounty[county];
          if (list.length === 0) return null;
          return <div key={county} data-id="element-3214" data-mp="35q75ig009fab">
                  <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-3" data-id="element-3215" data-mp="35p5vmeuq6yu6">
                    {county} County
                    <span className="ml-2 text-xs font-normal text-stone-400" data-id="element-3216" data-mp="2dzj7vdxjis1e">
                      {list.length} cit{list.length === 1 ? 'y' : 'ies'}
                    </span>
                  </h3>
                  <div className="space-y-3" data-id="element-3217" data-mp="28mchnl5m0e9h">
                    {list.map(city => <div key={city.slug} id={city.slug} className={`scroll-mt-24 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 border-l-4 ${ZONE_ACCENT[city.zone]}`} data-id="element-3218" data-mp="28nnjhffhvbxy">
                        <div className="flex items-center justify-between gap-3 mb-2" data-id="element-3219" data-mp="1yuf4i8v1jc84">
                          <div data-id="element-3220" data-mp="2mmvk8crb2s49">
                            <p className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-3221" data-mp="2rl6yr0sjpqst">
                              {city.name}
                            </p>
                            <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider" data-id="element-3222" data-mp="2cqaflsj77j0y">
                              Zone {city.zone} · {city.neighborhoods.length}+
                              neighborhoods
                            </p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-[10px] font-bold text-stone-700 dark:text-stone-200 shrink-0" data-id="element-3223" data-mp="38uj6zauutxo4">
                            {ZONE_FEE[city.zone]}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-id="element-3224" data-mp="2z2mw0z7o6d2h">
                          {products.map(({
                    product
                  }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="inline-flex items-center px-2.5 py-1 rounded-full bg-stone-100 dark:bg-white/[0.06] text-xs font-bold text-stone-700 dark:text-stone-200 hover:bg-brand-orange/10 hover:text-brand-orange transition-colors" data-id="element-3225" data-mp="2p71pqovkiqmx">
                              {product.name}
                            </Link>)}
                        </div>
                      </div>)}
                  </div>
                </div>;
        })}
          </div>
        </section>}

      {/* Empty state */}
      {totalMatches === 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-3226" data-mp="gz6k43epnl77">
          <div className="p-10 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 text-center" data-id="element-3227" data-mp="gx2jpvg0t4bo">
            <p className="text-sm text-stone-700 dark:text-stone-300" data-id="element-3228" data-mp="qo6pq5npxeud">
              No service areas match "{query}". Try a different city or
              neighborhood.
            </p>
            <button type="button" onClick={() => setQuery('')} className="mt-3 text-xs font-bold text-brand-orange hover:underline" data-id="element-3229" data-mp="3u5ov86g0q9rm">
              Clear search
            </button>
          </div>
        </section>}

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-3230" data-mp="7xfib9najyim">
        <div className="p-6 rounded-2xl bg-stone-900 dark:bg-black border border-stone-800 text-center" data-id="element-3231" data-mp="cxuwg3feda1n">
          <h2 className="font-heading text-xl font-bold text-white" data-id="element-3232" data-mp="1je6t202kmjv4">
            Not seeing your city?
          </h2>
          <p className="mt-2 text-sm text-stone-300" data-id="element-3233" data-mp="1ob5m0fai0krq">
            We deliver well beyond these cities. Check the zone map or call us
            for a custom quote.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3" data-id="element-3234" data-mp="1hx0571kxg7nr">
            <Link to="/delivery-zones" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-3235" data-mp="1vvpxm3wsxj5s">
              View Zone Map
              <ArrowRight className="w-3.5 h-3.5" data-id="element-3236" data-mp="24vmjxyvpy5xo" />
            </Link>
            <a href="tel:206-619-3742" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition-colors" data-id="element-3237" data-mp="20vcbmfkgf3fn">
              (206) 619-3742
            </a>
          </div>
        </div>
      </section>
    </main>;
}